import { useMutation } from "@tanstack/react-query";
import type { PayJunctionSDK, PayJunctionPaymentData } from "../types/payjunction";
import { cleanCardNumber, convertTwoDigitYearToFour } from "../utils/payment";

interface TokenizeResponse {
  tokenId: string;
}

/**
 * Secure PayJunction tokenization hook using the PayJunction JS SDK.
 *
 * ╔══════════════════════════════════════════════════════════════╗
 * ║  PCI DSS SAQ A - Card data is sent directly to PayJunction ║
 * ║  servers via their SDK. Our backend only receives tokenId.   ║
 * ╚══════════════════════════════════════════════════════════════╝
 *
 * According to PayJunction documentation:
 * https://developer.payjunction.com/hc/en-us/articles/4404250539291-JavaScript-Tokenization-Examples
 *
 * Usage:
 * 1. Initialize: PayJunction('PUBLISHABLE_KEY')
 * 2. Tokenize: payJunction.createToken(paymentData).then(...)
 *
 * The SDK sends card data directly to PayJunction's servers
 * and returns a one-time token. Only this token is passed
 * to our backend for vault creation.
 */
interface TokenizeParams {
  payJunctionInstance: PayJunctionSDK;
  paymentData: PayJunctionPaymentData;
}

export const usePayJunctionTokenize = () => {
  const { mutate, mutateAsync, isPending, isSuccess, isError, error, data } =
    useMutation<TokenizeResponse, Error, TokenizeParams>({
      mutationFn: async ({ payJunctionInstance, paymentData }) => {
        if (!payJunctionInstance?.createToken) {
          throw new Error("PayJunction SDK instance is not initialized");
        }

        const { cardNumber, cardExpMonth, cardExpYear, cardCvv } = paymentData;
        if (!cardNumber || !cardExpMonth || !cardExpYear || !cardCvv) {
          throw new Error("Payment card data is required for tokenization");
        }

        const result = await payJunctionInstance.createToken({
          cardNumber: cleanCardNumber(cardNumber),
          cardExpMonth,
          cardExpYear: cardExpYear.length === 2 
            ? convertTwoDigitYearToFour(cardExpYear) 
            : cardExpYear,
          cardCvv,
        });

        if (result.errors?.length) {
          const errorMessages = result.errors.map((err) => {
            if (err.parameter) {
              return `${err.parameter}: ${err.message}`;
            }
            return err.message;
          });
          
          const errorText = errorMessages.length === 1
            ? errorMessages[0]
            : errorMessages.join('; ');
          
          throw new Error(errorText);
        }

        if (!result.tokenId) {
          throw new Error("Tokenization succeeded but no tokenId was returned");
        }

        return { tokenId: result.tokenId };
      },
    });

  return {
    mutate,
    mutateAsync,
    isPending,
    isSuccess,
    isError,
    error,
    data,
  };
};
